import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { 
  Card, 
  CardContent, 
  Typography, 
  Avatar, 
  Chip, 
  Grid, 
  Box, 
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider
} from '@mui/material';
import { 
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  LocationOn as LocationIcon,
  AccountCircle as AccountCircleIcon,
  Security as SecurityIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import useAuth from '../../../features/hooks/useAuth';
import { useGetUsersQuery } from '../../../features/users/usersApiSlice';
import FilteredAssetsFetcher from '../../Assets/Myasset/FilteredAssetsFetcher';
import moment from 'moment';
import { HeadingWrap, Span } from '../../../Styles/CommonStyles';

// Styled Components
const Container = styled.div`
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
`;

const ProfileHeader = styled(Paper)`
  padding: 24px;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  color: #333;
  border-radius: 12px !important;
  border: 1px solid #e0e0e0;
`;

const ProfileAvatar = styled(Avatar)`
  width: 120px !important;
  height: 120px !important;
  margin-bottom: 16px;
  border: 4px solid rgba(25, 118, 210, 0.2) !important;
  font-size: 3rem !important;
  background-color: #1976d2 !important;
  color: white !important;
`;

const SectionCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  transition: transform 0.2s ease-in-out !important;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
`;

const SectionTitle = styled(Typography)`
  font-weight: 600 !important;
  color: #5C5891 !important;
  margin-left: 8px !important;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px 0;
`;

const InfoLabel = styled(Typography)`
  font-weight: 500 !important;
  color: #666 !important;
  min-width: 140px;
  display: flex;
  align-items: center;
`;

const InfoValue = styled(Typography)`
  color: #333 !important;
  margin-left: 16px !important;
`;

const RoleChip = styled(Chip)`
  margin: 4px !important;
  background-color: #e3f2fd !important;
  color: #1976d2 !important;
  font-weight: 500 !important;
`;

const OfficeChip = styled(Chip)`
  margin: 4px !important;
  background-color: #f3e5f5 !important;
  color: #7b1fa2 !important;
  font-weight: 500 !important;
`;

const StatusChip = styled(Chip)`
  font-weight: 600 !important;
`;

const ViewProfile = () => {
  const auth = useAuth();
  const [userDetails, setUserDetails] = useState(null);
  const [assetsData, setAssetsData] = useState({});
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);

  const { user } = useGetUsersQuery("usersList", {
    selectFromResult: ({ data }) => ({
      user: data?.entities
    }),
  });

  useEffect(() => {
    if (user && auth.id) {
      const currentUser = Object.values(user).find(u => u._id === auth.id);
      setUserDetails(currentUser);
      if (currentUser) {
        setIsLoadingAssets(true);
      }
    }
  }, [user, auth.id]);

  // Handle assets data fetched from FilteredAssetsFetcher
  const handleAssetsFetched = (data) => {
    console.log('Assets fetched:', data);
    setAssetsData(data || {});
    setIsLoadingAssets(false);
  };

  // Get user's offices (main charge and additional charge)
  const userOffices = [
    userDetails?.office?.mainCharge,
    ...(userDetails?.office?.additionalCharge || [])
  ].filter(Boolean);

  // Format assets by office and store using useMemo (similar to UserAssetsModal)
  const officeWiseAssets = useMemo(() => {
    if (!assetsData?.assets || !Array.isArray(assetsData.assets) || !userDetails) {
      return {};
    }

    // Group assets by office, then by store, then by category within each store
    return assetsData.assets.reduce((acc, asset) => {
      // Determine the office for this asset based on alloc_ofc field
      let assetOffice = asset.alloc_ofc || 'Unknown Office';
      
      // Fallback logic if alloc_ofc is not available
      if (!asset.alloc_ofc) {
        // If asset has cur_user_name and is assigned to user, try to determine office
        if (asset.cur_user_name) {
          // For individual or office assets, use the main office as fallback
          assetOffice = userDetails?.office?.mainCharge || 'Unknown Office';
        } 
        // If asset has cur_loc === 'office', it's a BO asset, use the office from asset
        else if (asset.cur_loc === 'office') {
          assetOffice = asset.cur_ofc || userDetails?.office?.mainCharge || 'Unknown Office';
        }
        // For store assets, use the main office as fallback
        else {
          assetOffice = userDetails?.office?.mainCharge || 'Unknown Office';
        }
      }

      // Only include assets from user's offices (main + additional charge)
      if (!userOffices.includes(assetOffice)) {
        return acc; // Skip assets not belonging to user's offices
      }

      if (!acc[assetOffice]) {
        acc[assetOffice] = {};
      }

      const storeName = asset.cur_store || 'Unknown Store';
      if (!acc[assetOffice][storeName]) {
        acc[assetOffice][storeName] = {
          individual: [],
          office: [],
          bo: [],
          store: []
        };
      }
      
      // Categorize assets within each store
      if (asset.indi_issue === true) {
        acc[assetOffice][storeName].individual.push(asset);
      }
      // Office assets (cur_loc === 'user')
      else if (asset.cur_loc === 'user') {
        acc[assetOffice][storeName].office.push(asset);
      }
      // BO assets (cur_loc === 'office')
      else if (asset.cur_loc === 'office') {
        acc[assetOffice][storeName].bo.push(asset);
      }
      // Store assets (only if user has ItemAdder_A role)
      else if (userDetails?.roles?.main?.includes('ItemAdder_A') || 
        Object.values(userDetails?.roles || {}).some(roleArray => 
          Array.isArray(roleArray) && roleArray.includes('ItemAdder_A')
        )) {
        acc[assetOffice][storeName].store.push(asset);
      }
      
      return acc;
    }, {});
  }, [assetsData, userDetails, userOffices]);

  if (!userDetails) {
    return (
      <Container>
        <Typography variant="h6" color="textSecondary">
          Loading profile...
        </Typography>
      </Container>
    );
  }

  const getInitials = (name) => {
    if (!name) return 'U';
    const { firstName = '', lastName = '' } = name;
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  const getFullName = (name) => {
    if (!name) return 'Unknown User';
    const { firstName = '', middleName = '', lastName = '' } = name;
    return [firstName, middleName, lastName].filter(Boolean).join(' ') || 'Unknown User';
  };

  const formatRoles = (roles) => {
    if (!roles) return [];
    const allRoles = [];
    
    // Add main roles
    if (roles.main) {
      allRoles.push(...roles.main.map(role => ({ role, office: 'System', type: 'main' })));
    }
    
    // Add office-specific roles
    Object.entries(roles).forEach(([office, officeRoles]) => {
      if (office !== 'main' && Array.isArray(officeRoles)) {
        allRoles.push(...officeRoles.map(role => ({ role, office, type: 'office' })));
      }
    });
    
    return allRoles;
  };

  const formatDesignations = (designations) => {
    if (!designations) return [];
    return Object.entries(designations).map(([office, designation]) => ({
      office,
      designation
    }));
  };

  const formatOffices = (office) => {
    if (!office) return { main: '', additional: [] };
    return {
      main: office.mainCharge || '',
      additional: office.additionalCharge || []
    };
  };

  // Create filters for the specific user and all their offices
  const userFilters = userDetails ? {
    cur_user_name: userDetails?.name?.firstName ? 
      `${userDetails.name.firstName} ${userDetails.name.middleName || ''} ${userDetails.name.lastName || ''}`.trim() : 
      userDetails?.email || '',
    // Filter by all user offices
    alloc_ofc: userOffices,
  } : null;

  // Check if user has ItemAdder_A role
  const hasItemAdderRole = userDetails?.roles?.main?.includes('ItemAdder_A') || 
    Object.values(userDetails?.roles || {}).some(roleArray => 
      Array.isArray(roleArray) && roleArray.includes('ItemAdder_A')
    );

  // Get office names with main office first, then additional offices sorted alphabetically
  const officeNames = Object.keys(officeWiseAssets).sort((a, b) => {
    const mainOffice = userDetails?.office?.mainCharge;
    
    // Main office comes first
    if (a === mainOffice) return -1;
    if (b === mainOffice) return 1;
    
    // Then sort additional offices alphabetically
    return a.localeCompare(b);
  });

  const totalAssets = Object.values(officeWiseAssets).reduce((officeSum, stores) => {
    return officeSum + Object.values(stores).reduce((storeSum, storeData) => {
      return storeSum + storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
    }, 0);
  }, 0);

  const allRoles = formatRoles(userDetails.roles);
  const designations = formatDesignations(userDetails.designation);
  const offices = formatOffices(userDetails.office);

  return (
    <Container>
      {/* Page Header */}
      <HeadingWrap>
        <Span>View Profile</Span>
      </HeadingWrap>

      {/* Hidden FilteredAssetsFetcher to fetch data */}
      {userFilters && (
        <FilteredAssetsFetcher 
          filters={userFilters} 
          onDataFetched={handleAssetsFetched} 
        />
      )}

      {/* Profile Header */}
      <ProfileHeader elevation={3}>
        <Box display="flex" alignItems="center">
          <ProfileAvatar>
            {getInitials(userDetails.name)}
          </ProfileAvatar>
          <Box ml={3}>
            <Typography variant="h4" component="h1" fontWeight="bold" mb={1} color="#333">
              {getFullName(userDetails.name)}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.8, color: '#555' }} mb={1}>
              {userDetails.email}
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <StatusChip 
                label={userDetails.active ? "Active" : "Inactive"}
                color={userDetails.active ? "success" : "error"}
                variant="filled"
              />
              <Box display="flex" alignItems="center" gap={1}>
                <InventoryIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                <Typography variant="body2" color="#666">
                  Total Assets: <strong>{totalAssets}</strong>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </ProfileHeader>

      <Grid container spacing={3}>
        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <SectionCard>
            <CardContent>
              <SectionHeader>
                <PersonIcon color="primary" />
                <SectionTitle variant="h6">Personal Information</SectionTitle>
              </SectionHeader>
              
              <InfoRow>
                <InfoLabel>
                  <AccountCircleIcon fontSize="small" sx={{ mr: 1 }} />
                  Full Name:
                </InfoLabel>
                <InfoValue>{getFullName(userDetails.name)}</InfoValue>
              </InfoRow>
              
              <InfoRow>
                <InfoLabel>
                  <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                  Email:
                </InfoLabel>
                <InfoValue>{userDetails.email}</InfoValue>
              </InfoRow>
              
              <InfoRow>
                <InfoLabel>
                  <BadgeIcon fontSize="small" sx={{ mr: 1 }} />
                  Employee ID:
                </InfoLabel>
                <InfoValue>{userDetails.employeeID || 'Not specified'}</InfoValue>
              </InfoRow>
              
              <InfoRow>
                <InfoLabel>
                  <SecurityIcon fontSize="small" sx={{ mr: 1 }} />
                  Status:
                </InfoLabel>
                <InfoValue>
                  <StatusChip 
                    label={userDetails.active ? "Active" : "Inactive"}
                    color={userDetails.active ? "success" : "error"}
                    size="small"
                  />
                </InfoValue>
              </InfoRow>
            </CardContent>
          </SectionCard>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <SectionCard>
            <CardContent>
              <SectionHeader>
                <PhoneIcon color="primary" />
                <SectionTitle variant="h6">Contact Information</SectionTitle>
              </SectionHeader>
              
              <InfoRow>
                <InfoLabel>
                  <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                  Mobile:
                </InfoLabel>
                <InfoValue>{userDetails.mobileNo || 'Not specified'}</InfoValue>
              </InfoRow>
              
              <InfoRow>
                <InfoLabel>
                  <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                  Office Landline:
                </InfoLabel>
                <InfoValue>{userDetails.officeLandLine || 'Not specified'}</InfoValue>
              </InfoRow>
              
              <InfoRow>
                <InfoLabel>
                  <LocationIcon fontSize="small" sx={{ mr: 1 }} />
                  Office Address:
                </InfoLabel>
                <InfoValue>{userDetails.officeAddress || 'Not specified'}</InfoValue>
              </InfoRow>
            </CardContent>
          </SectionCard>
        </Grid>

        {/* Office Assignments */}
        <Grid item xs={12} md={6}>
          <SectionCard>
            <CardContent>
              <SectionHeader>
                <BusinessIcon color="primary" />
                <SectionTitle variant="h6">Office Assignments</SectionTitle>
              </SectionHeader>
              
              <InfoRow>
                <InfoLabel>Main Charge:</InfoLabel>
                <InfoValue>
                  {offices.main ? (
                    <OfficeChip label={offices.main} variant="filled" />
                  ) : (
                    'Not assigned'
                  )}
                </InfoValue>
              </InfoRow>
              
              {offices.additional.length > 0 && (
                <InfoRow>
                  <InfoLabel>Additional Charge:</InfoLabel>
                  <Box ml={2}>
                    <Stack direction="row" flexWrap="wrap">
                      {offices.additional.map((office, index) => (
                        <OfficeChip key={index} label={office} variant="outlined" />
                      ))}
                    </Stack>
                  </Box>
                </InfoRow>
              )}
            </CardContent>
          </SectionCard>
        </Grid>

        {/* Designations */}
        <Grid item xs={12} md={6}>
          <SectionCard>
            <CardContent>
              <SectionHeader>
                <BadgeIcon color="primary" />
                <SectionTitle variant="h6">Designations</SectionTitle>
              </SectionHeader>
              
              {designations.length > 0 ? (
                designations.map((item, index) => (
                  <InfoRow key={index}>
                    <InfoLabel>{item.office}:</InfoLabel>
                    <InfoValue>
                      <Chip 
                        label={item.designation} 
                        color="primary" 
                        variant="outlined"
                        size="small"
                      />
                      {userDetails.designationSuffix?.[item.office] && (
                        <Chip 
                          label={userDetails.designationSuffix[item.office]} 
                          color="secondary" 
                          variant="outlined"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </InfoValue>
                  </InfoRow>
                ))
              ) : (
                <Typography color="textSecondary">No designations assigned</Typography>
              )}
            </CardContent>
          </SectionCard>
        </Grid>

        {/* Roles & Permissions */}
        <Grid item xs={12}>
          <SectionCard>
            <CardContent>
              <SectionHeader>
                <SecurityIcon color="primary" />
                <SectionTitle variant="h6">Roles & Permissions</SectionTitle>
              </SectionHeader>
              
              {allRoles.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" mb={2}>
                    System Roles:
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" mb={3}>
                    {allRoles
                      .filter(item => item.type === 'main')
                      .map((item, index) => (
                        <RoleChip 
                          key={index} 
                          label={item.role} 
                          color="primary"
                          variant="filled"
                        />
                      ))}
                  </Stack>
                  
                  {allRoles.some(item => item.type === 'office') && (
                    <>
                      <Typography variant="subtitle2" color="textSecondary" mb={2}>
                        Office-specific Roles:
                      </Typography>
                      <Box>
                        {Object.entries(
                          allRoles
                            .filter(item => item.type === 'office')
                            .reduce((acc, item) => {
                              if (!acc[item.office]) acc[item.office] = [];
                              acc[item.office].push(item.role);
                              return acc;
                            }, {})
                        ).map(([office, roles]) => (
                          <Box key={office} mb={2}>
                            <Typography variant="body2" fontWeight="500" mb={1}>
                              {office}:
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" ml={2}>
                              {roles.map((role, index) => (
                                <RoleChip 
                                  key={index} 
                                  label={role} 
                                  variant="outlined"
                                  size="small"
                                />
                              ))}
                            </Stack>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              ) : (
                <Typography color="textSecondary">No roles assigned</Typography>
              )}
            </CardContent>
          </SectionCard>
        </Grid>

        {/* Asset Details Section */}
        <Grid item xs={12}>
          <SectionCard>
            <CardContent>
              <SectionHeader>
                <InventoryIcon color="primary" />
                <SectionTitle variant="h6">Asset Details ({totalAssets} assets)</SectionTitle>
              </SectionHeader>
              
              {isLoadingAssets ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  py: 4,
                  gap: 2
                }}>
                  <CircularProgress size={60} />
                  <Typography variant="h6" color="text.secondary">
                    Loading Assets...
                  </Typography>
                </Box>
              ) : totalAssets === 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  py: 4,
                  gap: 2
                }}>
                  <InventoryIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                  <Typography variant="h6" color="text.secondary">
                    No Assets Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    You don't have any assigned assets in any of your offices.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Office-wise Assets Sections */}
                  {officeNames.map((officeName, officeIndex) => {
                    const stores = officeWiseAssets[officeName];
                    const storeNames = Object.keys(stores).sort();
                    const officeTotal = storeNames.reduce((sum, storeName) => {
                      const storeData = stores[storeName];
                      return sum + storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
                    }, 0);

                    if (officeTotal === 0) return null;

                    return (
                      <Box key={officeIndex} sx={{ 
                        border: '2px solid',
                        borderColor: 'primary.main',
                        borderRadius: 2,
                        overflow: 'hidden'
                      }}>
                        {/* Office Header */}
                        <Box sx={{ 
                          backgroundColor: 'primary.main', 
                          color: 'white', 
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2
                        }}>
                          <BusinessIcon sx={{ fontSize: 28 }} />
                          <Typography variant="h5" component="div">
                            Office: {officeName} ({officeTotal} assets)
                          </Typography>
                        </Box>

                        {/* Store-wise Assets within Office */}
                        <Box sx={{ p: 2 }}>
                          {storeNames.map((storeName, storeIndex) => {
                            const storeData = stores[storeName];
                            const storeTotal = storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
                            
                            if (storeTotal === 0) return null;

                            return (
                              <Box key={storeIndex} sx={{ mb: 3 }}>
                                {/* Store Header */}
                                <Box sx={{ 
                                  backgroundColor: 'secondary.main', 
                                  color: 'white', 
                                  p: 1.5,
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mb: 2
                                }}>
                                  <InventoryIcon sx={{ fontSize: 20 }} />
                                  <Typography variant="h6" component="div">
                                    Store: {storeName} ({storeTotal} assets)
                                  </Typography>
                                </Box>
                                
                                {/* Individual Assets Section */}
                                {storeData.individual.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                      <Typography variant="subtitle1" color="primary">
                                        Individual Assets ({storeData.individual.length})
                                      </Typography>
                                    </Box>
                                    <TableContainer component={Paper} variant="outlined">
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Sl.No.</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Asset Name</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>OEM Tag</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>OEM/Brand</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Model</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Purchase Date</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Warranty Date</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Issue Date</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {storeData.individual.map((asset, index) => (
                                            <TableRow key={index} hover>
                                              <TableCell>{index + 1}</TableCell>
                                              <TableCell>{asset.name || 'N/A'}</TableCell>
                                              <TableCell>{asset.oem_tag || 'N/A'}</TableCell>
                                              <TableCell>{asset.oem || 'N/A'}</TableCell>
                                              <TableCell>{asset.model_no || 'N/A'}</TableCell>
                                              <TableCell>
                                                {asset.purchase_date ? 
                                                  moment(asset.purchase_date).format("DD-MM-YYYY") : 'N/A'
                                                }
                                              </TableCell>
                                              <TableCell>
                                                {asset.warrantyexp_date ? 
                                                  moment(asset.warrantyexp_date).format("DD-MM-YYYY") : 'N/A'
                                                }
                                              </TableCell>
                                              <TableCell>
                                                {asset.last_trans_date ? 
                                                  moment(asset.last_trans_date).format("DD-MM-YYYY") : 'N/A'
                                                }
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </Box>
                                )}

                                {/* Office Assets Section */}
                                {storeData.office.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <BusinessIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                                      <Typography variant="subtitle1" color="secondary">
                                        Office Assets ({storeData.office.length})
                                      </Typography>
                                    </Box>
                                    <TableContainer component={Paper} variant="outlined">
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Sl.No.</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Asset Name</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>OEM Tag</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>OEM/Brand</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Model</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Purchase Date</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Warranty Date</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Issue Date</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {storeData.office.map((asset, index) => (
                                            <TableRow key={index} hover>
                                              <TableCell>{index + 1}</TableCell>
                                              <TableCell>{asset.name || 'N/A'}</TableCell>
                                              <TableCell>{asset.oem_tag || 'N/A'}</TableCell>
                                              <TableCell>{asset.oem || 'N/A'}</TableCell>
                                              <TableCell>{asset.model_no || 'N/A'}</TableCell>
                                              <TableCell>
                                                {asset.purchase_date ? 
                                                  moment(asset.purchase_date).format("DD-MM-YYYY") : 'N/A'
                                                }
                                              </TableCell>
                                              <TableCell>
                                                {asset.warrantyexp_date ? 
                                                  moment(asset.warrantyexp_date).format("DD-MM-YYYY") : 'N/A'
                                                }
                                              </TableCell>
                                              <TableCell>
                                                {asset.last_trans_date ? 
                                                  moment(asset.last_trans_date).format("DD-MM-YYYY") : 'N/A'
                                                }
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </Box>
                                )}

                                {/* BO Assets Section */}
                                {storeData.bo.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <BusinessIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                                      <Typography variant="subtitle1" color="warning.main">
                                        BO Assets ({storeData.bo.length})
                                      </Typography>
                                    </Box>
                                    <TableContainer component={Paper} variant="outlined">
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Sl.No.</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Asset Name</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>OEM Tag</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>OEM/Brand</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Model</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Purchase Date</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Warranty Date</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Issue Date</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {storeData.bo.map((asset, index) => (
                                            <TableRow key={index} hover>
                                              <TableCell>{index + 1}</TableCell>
                                              <TableCell>{asset.name || 'N/A'}</TableCell>
                                              <TableCell>{asset.oem_tag || 'N/A'}</TableCell>
                                              <TableCell>{asset.oem || 'N/A'}</TableCell>
                                              <TableCell>{asset.model_no || 'N/A'}</TableCell>
                                              <TableCell>
                                                {asset.purchase_date ? 
                                                  moment(asset.purchase_date).format("DD-MM-YYYY") : 'N/A'
                                                }
                                              </TableCell>
                                              <TableCell>
                                                {asset.warrantyexp_date ? 
                                                  moment(asset.warrantyexp_date).format("DD-MM-YYYY") : 'N/A'
                                                }
                                              </TableCell>
                                              <TableCell>
                                                {asset.last_trans_date ? 
                                                  moment(asset.last_trans_date).format("DD-MM-YYYY") : 'N/A'
                                                }
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </Box>
                                )}

                                {/* Store Assets Section */}
                                {storeData.store.length > 0 && hasItemAdderRole && (
                                  <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <InventoryIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                      <Typography variant="subtitle1" color="success.main">
                                        Store Assets ({storeData.store.length}) - Only visible for ItemAdder_A role
                                      </Typography>
                                    </Box>
                                    <TableContainer component={Paper} variant="outlined">
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Sl.No.</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Asset Name</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>OEM Tag</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>OEM/Brand</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Model</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Purchase Date</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Warranty Date</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Issue Date</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {storeData.store.map((asset, index) => (
                                            <TableRow key={index} hover>
                                              <TableCell>{index + 1}</TableCell>
                                              <TableCell>{asset.name || 'N/A'}</TableCell>
                                              <TableCell>{asset.oem_tag || 'N/A'}</TableCell>
                                              <TableCell>{asset.oem || 'N/A'}</TableCell>
                                              <TableCell>{asset.model_no || 'N/A'}</TableCell>
                                              <TableCell>
                                                {asset.purchase_date ? 
                                                  moment(asset.purchase_date).format("DD-MM-YYYY") : 'N/A'
                                                }
                                              </TableCell>
                                              <TableCell>
                                                {asset.warrantyexp_date ? 
                                                  moment(asset.warrantyexp_date).format("DD-MM-YYYY") : 'N/A'
                                                }
                                              </TableCell>
                                              <TableCell>
                                                {asset.last_trans_date ? 
                                                  moment(asset.last_trans_date).format("DD-MM-YYYY") : 'N/A'
                                                }
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}

                  {/* Asset Summary Section */}
                  {totalAssets > 0 && (
                    <Box sx={{ 
                      mt: 2, 
                      p: 2, 
                      backgroundColor: 'grey.50', 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Asset Summary
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {officeNames.map((officeName, officeIndex) => {
                          const stores = officeWiseAssets[officeName];
                          const storeNames = Object.keys(stores).sort();
                          const officeTotal = storeNames.reduce((sum, storeName) => {
                            const storeData = stores[storeName];
                            return sum + storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
                          }, 0);
                          
                          if (officeTotal === 0) return null;
                          
                          return (
                            <Box key={officeIndex} sx={{ 
                              p: 1, 
                              backgroundColor: 'white', 
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.300'
                            }}>
                              <Typography variant="subtitle2" color="primary" gutterBottom>
                                <strong>{officeName}</strong> - Total: {officeTotal}
                              </Typography>
                              {storeNames.map((storeName, storeIndex) => {
                                const storeData = stores[storeName];
                                const storeTotal = storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
                                
                                if (storeTotal === 0) return null;
                                
                                return (
                                  <Box key={storeIndex} sx={{ ml: 2, mb: 1 }}>
                                    <Typography variant="body2" color="secondary" gutterBottom>
                                      <strong>Store: {storeName}</strong> - Total: {storeTotal}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', ml: 2 }}>
                                      {storeData.individual.length > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <PersonIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                          <Typography variant="body2">
                                            Individual: <strong>{storeData.individual.length}</strong>
                                          </Typography>
                                        </Box>
                                      )}
                                      {storeData.office.length > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <BusinessIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                                          <Typography variant="body2">
                                            Office: <strong>{storeData.office.length}</strong>
                                          </Typography>
                                        </Box>
                                      )}
                                      {storeData.bo.length > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <BusinessIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                          <Typography variant="body2">
                                            BO: <strong>{storeData.bo.length}</strong>
                                          </Typography>
                                        </Box>
                                      )}
                                      {storeData.store.length > 0 && hasItemAdderRole && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <InventoryIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                          <Typography variant="body2">
                                            Store: <strong>{storeData.store.length}</strong>
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        })}
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1 }}>
                          <InventoryIcon sx={{ fontSize: 20, color: 'info.main' }} />
                          <Typography variant="body2">
                            Total Assets: <strong>{totalAssets}</strong>
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </SectionCard>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ViewProfile;